/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL || "";

// VRF Configuration interface for cluster mode
// Interface for VRF form data (from UI)
export interface VRFFormData {
  name: string;
  routeTargets: string[];
  newRouteTarget: string;
  routingTable: string;
  nextHopEni: string;
  policySelection: string;
  nextHopSelection: string;
  selectedRoutingTable?: string;
  selectedEni?: string;
  routingTableTagName?: string;
  routingTableTagValue?: string;
  nextHopTagName?: string;
  nextHopTagValue?: string;
  routingTables: string[];
  enis: string[];
  expanded: boolean;
  selectedVpc?: string; // Add VPC selection for Telco mode
  inboundId?: number | null; // Add inboundId for Telco mode updates
}

export interface VRFClusterConfig {
  vrfName: string;
  routeTargets: string;
  routingTableType: "dynamic" | "specific";
  routingTagName?: string;
  routingTagValue?: string;
  routingTables?: string[];
  nextHopType: "TAG" | "ENI";
  nextHopTagName?: string;
  nextHopTagValue?: string;
  eni?: string; // Changed from enis to eni for Telco mode
  mode: "TELCO" | "ENTERPRISE";
  vpcId?: string; // Add vpcId field for each VRF
  inboundId?: number | null; // Add inboundId for Telco mode updates
}

// Cluster Propagation Rule Payload Interfaces
export interface InboundPropagationRuleClusterPayload {
  // Mode
  mode?: "TELCO" | "ENTERPRISE";
  
  // Enterprise Dynamic Routing
  routingTableType?: "dynamic" | "specific";
  routingTagName?: string;
  routingTagValue?: string;
  routingTables?: string[];
  
  // Next Hop Configuration
  nextHopType?: "TAG" | "ENI";
  nextHopTagName?: string;
  nextHopTagValue?: string;
  eni?: string;
  
  // Telco Mode - Single VRF (backward compatibility)
  vrfName?: string;
  routeTargets?: string;
  
  // Telco Mode - Multiple VRFs
  vrfs?: VRFClusterConfig[];
  
  // VPC Selection for both Enterprise and Telco modes
  vpcId?: string;
}

export interface OutboundPropagationRuleClusterPayload {
  // Mode
  mode?: "TELCO" | "ENTERPRISE";
  
  // Subnet Configuration
  subnets?: string[];
  
  // BGP Community
  bgpCommunity?: string;
  
  // Next Hop Configuration
  nexthopIPAddress?: string;
}

// Helper functions to build cluster payloads
export const buildInboundPropagationRuleClusterPayload = (
  values: any, 
  mode: string
): InboundPropagationRuleClusterPayload => {
  // Normalize mode to uppercase for API consistency
  const normalizedMode = mode?.toUpperCase() === "TELCO" ? "TELCO" : "ENTERPRISE";
  const isTelco = normalizedMode === "TELCO";
  if (isTelco) {
    // Telco mode payload - handle multiple VRFs
    if (values.vrfs && values.vrfs.length > 1) {
      // Multiple VRFs - create array of VRF configurations
      const vrfConfigs = values.vrfs.map((vrf: VRFFormData) => {
        const vrfConfig: VRFClusterConfig = {
          vrfName: vrf.name,
          routeTargets: vrf.routeTargets?.join(", ") || "",
          routingTableType: vrf.policySelection === "specific" ? "specific" : "dynamic",
          nextHopType: vrf.nextHopSelection === "specific" ? "ENI" : "TAG",
          mode: normalizedMode,
          vpcId: vrf.selectedVpc, // Add vpcId inside each VRF
          inboundId: vrf.inboundId || null, // Add inboundId for updates
        };
        
        // Handle routing table configuration
        if (vrf.policySelection === "specific") {
          vrfConfig.routingTables = vrf.routingTables || [];
        } else {
          vrfConfig.routingTagName = vrf.routingTableTagName;
          vrfConfig.routingTagValue = vrf.routingTableTagValue;
        }
        
        // Handle next-hop configuration
        if (vrf.nextHopSelection === "specific") {
          // For Telco mode, send only the first ENI as a string
          vrfConfig.eni = vrf.enis && vrf.enis.length > 0 ? vrf.enis[0] : undefined;
        } else {
          vrfConfig.nextHopTagName = vrf.nextHopTagName;
          vrfConfig.nextHopTagValue = vrf.nextHopTagValue;
        }
        
        return vrfConfig;
      });
      
      return {
        mode: normalizedMode,
        vrfs: vrfConfigs,
      };
    } else {
      // Single VRF - always use vrfs array format for Telco mode
      const vrf = values.vrfs?.[0];
      if (!vrf) {
        throw new Error("No VRF data provided for Telco mode");
      }
      
      const vrfConfig: VRFClusterConfig = {
        vrfName: vrf.name || "Default VRF",
        routeTargets: vrf.routeTargets?.join(", ") || "",
        routingTableType: vrf.policySelection === "specific" ? "specific" : "dynamic",
        nextHopType: vrf.nextHopSelection === "specific" ? "ENI" : "TAG",
        mode: normalizedMode,
        vpcId: vrf.selectedVpc, // Add vpcId inside each VRF
        inboundId: vrf.inboundId || null, // Add inboundId for updates
      };
      
      // Handle routing table configuration
      if (vrf.policySelection === "specific") {
        vrfConfig.routingTables = vrf.routingTables || [];
      } else {
        vrfConfig.routingTagName = vrf.routingTableTagName;
        vrfConfig.routingTagValue = vrf.routingTableTagValue;
      }
      
      // Handle next-hop configuration
      if (vrf.nextHopSelection === "specific") {
        // For Telco mode, send only the first ENI as a string
        vrfConfig.eni = vrf.enis && vrf.enis.length > 0 ? vrf.enis[0] : undefined;
      } else {
        vrfConfig.nextHopTagName = vrf.nextHopTagName;
        vrfConfig.nextHopTagValue = vrf.nextHopTagValue;
      }
      
      return {
        mode: normalizedMode,
        vrfs: [vrfConfig],
      };
    }
  } else {
    // Enterprise mode payload
    const payload: InboundPropagationRuleClusterPayload = {
      mode: normalizedMode,
      routingTableType: values.policySelection === "specific" ? "specific" : "dynamic",
      routingTagName: values.policySelection === "dynamic" ? values.routingTableTagName : undefined,
      routingTagValue: values.policySelection === "dynamic" ? values.routingTableTagValue : undefined,
      routingTables: values.policySelection === "specific" ? values.routingTables : undefined,
      nextHopType: values.nextHopSelection === "specific" ? "ENI" : "TAG",
      nextHopTagName: values.nextHopSelection === "dynamic" ? values.nextHopTagName : undefined,
      nextHopTagValue: values.nextHopSelection === "dynamic" ? values.nextHopTagValue : undefined,
      eni: values.nextHopSelection === "specific" && values.enis && values.enis.length > 0 ? values.enis[0] : undefined,
    };
    
    // Enterprise mode - add routeTargets if available (for consistency with Telco mode)
    if (values.routeTargets && values.routeTargets.length > 0) {
      payload.routeTargets = values.routeTargets.join(", ");
    }
    
    // Add VPC selection for Enterprise mode
    if (values.selectedVpc) {
      payload.vpcId = values.selectedVpc;
    }
    
    return payload;
  }
};

export const buildOutboundPropagationRuleClusterPayload = (
  values: any,
  mode = "ENTERPRISE"
): OutboundPropagationRuleClusterPayload => {
  // Normalize mode to uppercase for API consistency
  const normalizedMode = mode?.toUpperCase() === "TELCO" ? "TELCO" : "ENTERPRISE";
  
  return {
    mode: normalizedMode,
    subnets: values.subnets || [],
    bgpCommunity: values.bgpCommunity || "",
    nexthopIPAddress: values.nexthopIPAddress || "",
  };
};

interface ClusterInstance {
    clusterId: number;
    reflectors: number[];
}

export interface ReflectorBgpConfig {
    localAs: number;
    routerId?: string;
    routerId1?: string;
    routerId2?: string;
}
interface ReflectorRole {
    reflectorId: number;
    role: string;
}

interface Cluster {
    description: string;
    region: string;
    cloud: string;
    status: string;
    reflectors: ReflectorRole[];
}

/**GET /clusters
  Gets all clusters for the authenticated tenant with their associated instances.
 */
export function getClusters(token: string, tenantId: number) {
    return axios.get(`${API_URL}/v2/reflector/clusters?tenantId=${tenantId}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

export function getClusterBgpNeighbors(token: string, clusterId: number) {
    return axios.get(`${API_URL}/v2/reflector/clusters/${clusterId}/bgp-neighbors/grouped`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**GET /clusters/:id
Gets specific cluster details with its instances.
 */
export function getClusterById(token: string, id: string) {
    return axios.get(`${API_URL}/v2/reflector/clusters/${id}`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**POST /clusters/add-reflector
  Creates a new cluster with the given reflectors.
 */
export function addReflectorsToCluster(token: string, payload: ClusterInstance) {
    return axios.post(`${API_URL}/v2/reflector/clusters/add-reflector`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**POST reflector/clusters
 * Creates a new cluster with the given reflectors.
 */
export function createCluster(token: string, payload: Cluster) {
    return axios.post(`${API_URL}/v2/reflector/clusters`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/** PUT v2/reflector/clusters/:id/name 
 * Updates the name of a specific Cluster.
*/
export function updateClusterName(token: string, clusterId: number, payload: { name: string }) {
    return axios.put(`${API_URL}/v2/reflector/clusters/${clusterId}/name`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**PUT /v2/reflector/clusters/:id/tags
 * Updates the tags of a specific Cluster.  
 */
export function updateClusterTags(token: string, clusterId: number, payload: { tags: string }) {
    return axios.put(`${API_URL}/v2/reflector/clusters/${clusterId}/tags`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**PUT /v2/reflector/clusters/:clusterId/update-roles 
 * Updates the roles of the reflectors in a cluster.
 */
export function updateClusterRoles(token: string, clusterId: number, payload: { roles: ReflectorRole[] }) {
    return axios.put(`${API_URL}/v2/reflector/clusters/${clusterId}/update-roles`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}
//BGP CONFIGURATION

/**GET /v2/reflector/clusters/:id/bgp-config
 * Gets the BGP configuration for a specific cluster.
 */
export function getBgpConfig(token: string, clusterId: number) {
    return axios.get(`${API_URL}/v2/reflector/clusters/${clusterId}/bgp-config`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**POST /v2/reflector/clusters/:id/bgp-config
 * Creates a new BGP configuration for a specific cluster.
 */

export function createBgpConfig(token: string, clusterId: number, payload: ReflectorBgpConfig) {
    return axios.post(`${API_URL}/v2/reflector/clusters/${clusterId}/bgp-config`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}

/**PUT /v2/reflector/clusters/:id/bgp-config
 * Updates the BGP configuration for a specific cluster.
 */
export function updateBgpConfig(token: string, clusterId: number, payload: ReflectorBgpConfig) {
    return axios.put(`${API_URL}/v2/reflector/clusters/${clusterId}/bgp-config`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    })
}



//CLOUD PROPAGATION

/**
 * GET /v2/reflector/clusters/:clusterId/propagation-rules
 * Gets cloud propagation rules for cluster
 */
export function getCloudPropagationRulesByCluster(token: string, clusterId:number){
    return axios.get(`${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/grouped`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
/**
 * CREATE INBOUND Propagation Rule Cluster
 * POST v2/reflector/clusters/:clusterId/propagation-rules/inbound
 */

export function createInboundPropagationRuleCluster(token: string, clusterId: number, payload: InboundPropagationRuleClusterPayload) {
    return axios.post(`${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/inbound`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * CREATE OUTBOUND Propagation Rule Cluster
 * POST v2/reflector/clusters/:clusterId/propagation-rules/outbound
 */

export function createOutboundPropagationRuleCluster(token: string, clusterId: number, payload: OutboundPropagationRuleClusterPayload) {
    return axios.post(`${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/outbound`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * PUT /v2/reflector/clusters/:clusterId/propagation-rules/inbound/:inboundId
 * Update inbound propagation rule
 */
export function updateInboundPropagationRuleCluster(token: string, clusterId: number, inboundId: number, payload: InboundPropagationRuleClusterPayload) {
    return axios.put(`${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/inbound/${inboundId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * PUT v2/reflector/clusters/:clusterId/propagation-rules/outbound/:outboundId
 * Update outbound propagation rule
 */
export function updateOutboundPropagationRuleCluster(token: string, clusterId: number, outboundId: number, payload: OutboundPropagationRuleClusterPayload) {
    return axios.put(`${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/outbound/${outboundId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
